/**
 * GraphQL Endpoint Config — Proxy endpoint constants
 *
 * All GraphQL calls go through Next.js server-side proxy routes.
 * The proxy injects auth tokens from Redis and forwards to the correct backend.
 *
 * Two backend targets:
 *   - 'gateway': API Gateway (main GraphQL federation — catalog, CMS, auth, etc.)
 *   - 'commerce': Commerce backend direct (cart, checkout, payment — bypasses gateway)
 *
 * Usage in modules:
 *   import { GRAPHQL_PROXY_ENDPOINTS, useGraphQlEndpoint } from '@kidslabs/module-core';
 *   const uri = useGraphQlEndpoint('commerce');
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type GraphQlTarget = 'gateway' | 'commerce';

// ─── Proxy Endpoints ──────────────────────────────────────────────────────────

export const GRAPHQL_PROXY_ENDPOINTS: Record<GraphQlTarget, string> = {
    gateway: '/api/v2/graphql',
    commerce: '/api/v2/graphql-commerce',
} as const;

// ─── React Hook ───────────────────────────────────────────────────────────────

/**
 * Hook to get the GraphQL proxy endpoint URL for a given target.
 *
 * @param target - 'gateway' (API Gateway) or 'commerce' (Commerce direct)
 * @returns The proxy endpoint URL
 *
 * @example
 *   // For API Gateway (default)
 *   const uri = useGraphQlEndpoint();
 *
 *   // For Commerce direct (cart, checkout)
 *   const uri = useGraphQlEndpoint('commerce');
 */
export function useGraphQlEndpoint(target: GraphQlTarget = 'gateway'): string {
    return GRAPHQL_PROXY_ENDPOINTS[target];
}
